import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  StickyNote,
  Clock,
  CheckCircle2,
  XCircle,
  Calendar,
  DollarSign,
  Archive,
  AlertCircle,
  Activity,
  User as UserIcon,
  Tag as TagIcon,
  Phone,
  MessageSquare,
  ShieldAlert,
  RefreshCw,
  Bell,
  Send,
  DoorOpen,
  Bot,
  Shield,
  Target,
  LayoutGrid,
  ScrollText,
  Trash2,
  Edit3,
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useTheme } from '../contexts/ThemeContext.tsx';
import api, {
  getContactAuditLog,
  obterLead,
  listarAgendamentos,
  listarComparecimentos,
  listarVendas,
  listarNoShows,
  getCrmAuditEvents,
  API_URL,
} from '../services/api.ts';
import type { CrmAuditEvent } from '../services/api.ts';
import ImagePreviewModal from './ImagePreviewModal.tsx';
import type {
  AgendamentoResponse,
  Comparecimento,
  Venda,
  NoShowEvent,
  Lead,
} from '../services/api.ts';

interface LeadTimelineProps {
  contactId: number;
  contactPhone: string;
  clinicId: number;
  leadId?: number | null;
  refreshToken?: number;
}

type Tone = 'brand' | 'success' | 'warning' | 'danger' | 'neutral' | 'purple' | 'pink';
type ActorType =
  | 'ia'
  | 'user'
  | 'master'
  | 'meta_ads'
  | 'whatsapp'
  | 'crm'
  | 'historical'
  | 'system';

interface TimelineItem {
  id: string;
  createdAt: string;
  icon: any;
  tone: Tone;
  title: string;
  description?: string;
  metaLines?: string[];
  actorType: ActorType;
  actorLabel?: string;
  sourceId?: string;
}

interface AuditEntry {
  id?: number;
  action_type: string;
  action_details?: any;
  created_at: string;
  user_name?: string;
  user_kind?: string;
}

interface NoteResponse {
  id: number;
  content: string;
  created_at: string;
  updated_at: string;
  created_by?: { name?: string; type?: string };
}

interface TaskItem {
  id: number;
  task_type: string;
  title: string;
  description?: string;
  scheduled_for: string;
  message_type?: string;
  message_content?: string;
  status: string;
  created_at: string;
  created_by?: { name?: string; email?: string; type?: string };
}

const ACTION_LABELS: Record<string, { label: string; icon: any; tone: Tone }> = {
  attendance_claim: { label: 'Atendimento assumido', icon: UserIcon, tone: 'danger' },
  attendance_release: { label: 'Atendimento liberado para a fila', icon: UserIcon, tone: 'neutral' },
  attendance_transfer: { label: 'Atendimento transferido', icon: UserIcon, tone: 'brand' },
  attendance_close: { label: 'Conversa finalizada', icon: CheckCircle2, tone: 'neutral' },
  attendance_reopen: { label: 'Conversa reaberta', icon: DoorOpen, tone: 'brand' },
  archive: { label: 'Contato arquivado', icon: Archive, tone: 'neutral' },
  unarchive: { label: 'Contato desarquivado', icon: Archive, tone: 'brand' },
  human_takeover: { label: 'Atendimento humano ativado', icon: UserIcon, tone: 'warning' },
  release_to_bot: { label: 'Atendimento devolvido para a IA', icon: Activity, tone: 'brand' },
  human_outbound: { label: 'Mensagem enviada por atendente', icon: Send, tone: 'brand' },
  flow_paused: { label: 'Fluxo pausado', icon: AlertCircle, tone: 'warning' },
  flow_resumed: { label: 'Fluxo retomado', icon: Activity, tone: 'success' },
  tag_added: { label: 'Tag adicionada', icon: TagIcon, tone: 'purple' },
  tag_removed: { label: 'Tag removida', icon: TagIcon, tone: 'neutral' },
  call: { label: 'Ligação enviada', icon: Phone, tone: 'success' },
  nps_sent: { label: 'NPS enviado', icon: MessageSquare, tone: 'brand' },
};

const parseDetails = (details: any): any => {
  if (!details) return null;
  if (typeof details === 'string') {
    try {
      return JSON.parse(details);
    } catch {
      return null;
    }
  }
  if (typeof details === 'object') return details;
  return null;
};

const auditRows = (data: any): AuditEntry[] => {
  if (Array.isArray(data)) return data;
  return data?.audit_logs || data?.entries || data?.logs || [];
};

const FLOW_TYPE_LABELS: Record<string, string> = {
  follow_up: 'Follow-up',
  noshow: 'No-show',
  confirmation: 'Confirmação',
  pos_consulta: 'Pós-consulta',
  pos_venda: 'Pós-venda',
  all: 'Todos os fluxos',
};

const formatActionType = (
  raw: string,
  details?: any
): { label: string; icon: any; tone: Tone } => {
  if (raw === 'tag_added' || raw === 'tag_removed') {
    const parsed = parseDetails(details);
    const tagName = parsed?.tag_name;
    return {
      label: raw === 'tag_added'
        ? (tagName ? `Tag adicionada: ${tagName}` : 'Tag adicionada')
        : (tagName ? `Tag removida: ${tagName}` : 'Tag removida'),
      icon: TagIcon,
      tone: raw === 'tag_added' ? 'purple' : 'neutral',
    };
  }
  if (raw === 'flow_paused' || raw === 'flow_resumed') {
    const parsed = parseDetails(details);
    const ft = parsed?.flow_type;
    const ftLabel = ft ? (FLOW_TYPE_LABELS[ft] || ft) : '';
    return {
      label: raw === 'flow_paused'
        ? (ftLabel ? `Fluxo pausado: ${ftLabel}` : 'Fluxo pausado')
        : (ftLabel ? `Fluxo retomado: ${ftLabel}` : 'Fluxo retomado'),
      icon: raw === 'flow_paused' ? AlertCircle : Activity,
      tone: raw === 'flow_paused' ? 'warning' : 'success',
    };
  }
  if (ACTION_LABELS[raw]) return ACTION_LABELS[raw];
  return {
    label: raw.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
    icon: ShieldAlert,
    tone: 'neutral',
  };
};

const stringifyDetails = (details: any): string | undefined => {
  if (!details) return undefined;
  if (typeof details === 'string') return details;
  if (typeof details === 'object') {
    for (const f of ['message', 'reason', 'description', 'note', 'detail']) {
      if (typeof details[f] === 'string') return details[f];
    }
    try {
      return JSON.stringify(details);
    } catch {
      return undefined;
    }
  }
  return String(details);
};

const toneRingClasses = (tone: Tone, isDark: boolean): string => {
  switch (tone) {
    case 'brand':
      return 'bg-sminy-primary ring-sminy-primary/30';
    case 'success':
      return isDark ? 'bg-green-400 ring-green-400/30' : 'bg-green-500 ring-green-500/30';
    case 'warning':
      return isDark ? 'bg-yellow-400 ring-yellow-400/30' : 'bg-yellow-500 ring-yellow-500/30';
    case 'danger':
      return isDark ? 'bg-red-400 ring-red-400/30' : 'bg-red-500 ring-red-500/30';
    case 'purple':
      return isDark ? 'bg-purple-400 ring-purple-400/30' : 'bg-purple-500 ring-purple-500/30';
    case 'pink':
      return isDark ? 'bg-pink-400 ring-pink-400/30' : 'bg-pink-500 ring-pink-500/30';
    case 'neutral':
    default:
      return isDark ? 'bg-gray-500 ring-gray-500/30' : 'bg-gray-400 ring-gray-400/30';
  }
};

const toneIconBg = (tone: Tone, isDark: boolean): string => {
  switch (tone) {
    case 'brand':
      return isDark ? 'bg-sminy-primary/15 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary';
    case 'success':
      return isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-600';
    case 'warning':
      return isDark ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-50 text-yellow-600';
    case 'danger':
      return isDark ? 'bg-red-900/40 text-red-300' : 'bg-red-50 text-red-600';
    case 'purple':
      return isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-50 text-purple-600';
    case 'pink':
      return isDark ? 'bg-pink-900/40 text-pink-300' : 'bg-pink-50 text-pink-600';
    case 'neutral':
    default:
      return isDark ? 'bg-gray-700/60 text-gray-300' : 'bg-gray-100 text-gray-600';
  }
};

const formatBR = (raw?: string): string => {
  if (!raw) return '';
  try {
    const d = new Date(raw.replace(' ', 'T'));
    return format(d, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return raw;
  }
};

const formatBRL = (n?: number): string => {
  if (n === undefined || n === null) return '';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// IDs numéricos longos (15+ dígitos) são típicos de campanhas/anúncios Meta (Facebook/Instagram).
// Usado para reclassificar leads que entraram via WhatsApp (Click-to-WhatsApp) mas vieram de um anúncio Meta.
const isMetaAdSourceId = (sourceId?: string | null): boolean => {
  if (!sourceId) return false;
  return /^\d{15,}$/.test(sourceId.trim());
};

const deriveActor = (
  audit: { user_name?: string },
  byTypeFields?: {
    created_by_type?: string;
    created_by_agent?: boolean;
    created_via?: string;
    creator_name?: string;
    user_name?: string;
    name?: string;
    email?: string;
  },
  sourceId?: string | null
): { actorType: ActorType; actorLabel?: string } => {
  const f = byTypeFields || {};
  const via = (f.created_via || '').toLowerCase();
  const type = (f.created_by_type || '').toLowerCase();

  // 1) IA (agente automático)
  if (f.created_by_agent === true || type === 'agent' || type === 'ai' || via === 'ai_agent' || via === 'agent') {
    return { actorType: 'ia', actorLabel: 'IA' };
  }

  // 2) Anúncio Meta Ads (Facebook/Instagram) — também detecta Click-to-WhatsApp pelo formato do source_id
  if (via === 'meta_ads' || via === 'facebook' || via === 'instagram' || isMetaAdSourceId(sourceId)) {
    return { actorType: 'meta_ads', actorLabel: 'Anúncio Meta Ads' };
  }

  // 3) Mensagem do contato no WhatsApp (lead criado pelo próprio cliente entrando em contato)
  if (via === 'whatsapp' || via === 'webhook' || type === 'webhook') {
    return { actorType: 'whatsapp', actorLabel: 'Mensagem WhatsApp' };
  }

  // 4) Master (dono da clínica)
  if (type === 'client' || type === 'master') {
    return { actorType: 'master', actorLabel: f.creator_name || f.name || f.email || 'Gestor' };
  }

  // 5) Usuário (atendente da equipe)
  if (type === 'user') {
    return { actorType: 'user', actorLabel: f.creator_name || f.name || f.email || 'Atendente' };
  }

  // 6) Automação interna identificada de forma durável no audit log
  if (type === 'system') {
    return {
      actorType: 'system',
      actorLabel: f.creator_name || f.name || f.email || 'Sistema',
    };
  }

  // 7) Usuário identificado via audit log
  if (audit.user_name) {
    return { actorType: 'user', actorLabel: audit.user_name };
  }
  if (f.creator_name) {
    return { actorType: 'user', actorLabel: f.creator_name };
  }

  // 8) Cadastro manual no CRM
  if (via === 'crm_manual' || via === 'crm') {
    return { actorType: 'crm', actorLabel: 'Cadastro pelo CRM' };
  }

  // 9) Registro histórico (existia antes do rastreamento)
  if (via === 'historical' || type === 'unknown_historical' || type === 'historical') {
    return { actorType: 'historical', actorLabel: 'Registro antigo' };
  }

  // 10) Default
  return { actorType: 'system', actorLabel: 'Sistema' };
};

const ActorBadge: React.FC<{ actorType: ActorType; label?: string; isDark: boolean }> = ({
  actorType,
  label,
  isDark,
}) => {
  let icon: any = Bot;
  let cls = isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500';
  switch (actorType) {
    case 'ia':
      icon = Bot;
      cls = isDark ? 'bg-sminy-primary/15 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary';
      break;
    case 'user':
      icon = UserIcon;
      cls = isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700';
      break;
    case 'master':
      icon = Shield;
      cls = isDark ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-50 text-purple-700';
      break;
    case 'meta_ads':
      icon = Target;
      cls = isDark ? 'bg-sminy-primary/15 text-sminy-primary' : 'bg-sminy-primary/10 text-sminy-primary';
      break;
    case 'whatsapp':
      icon = MessageSquare;
      cls = isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-50 text-green-600';
      break;
    case 'crm':
      icon = LayoutGrid;
      cls = isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700';
      break;
    case 'historical':
      icon = ScrollText;
      cls = isDark ? 'bg-gray-700/60 text-gray-400' : 'bg-gray-100 text-gray-500';
      break;
    case 'system':
    default:
      // mantém defaults
      break;
  }
  const Icon: any = icon || Bot;
  const fallbackLabel: Record<ActorType, string> = {
    ia: 'IA',
    user: 'Atendente',
    master: 'Gestor',
    meta_ads: 'Anúncio Meta Ads',
    whatsapp: 'Mensagem WhatsApp',
    crm: 'Cadastro pelo CRM',
    historical: 'Registro antigo',
    system: 'Sistema',
  };

  return (
    <span className={`inline-flex max-w-full items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${cls}`}>
      <Icon className="w-3 h-3 shrink-0" />
      <span className="min-w-0 break-words [overflow-wrap:anywhere]">{label || fallbackLabel[actorType] || 'Sistema'}</span>
    </span>
  );
};

// v2: defensive icon fallback
interface EventAdThumbnailProps {
  sourceId: string;
  clinicId: number;
  isDark: boolean;
  onClick: () => void;
}

const EventAdThumbnail: React.FC<EventAdThumbnailProps> = ({ sourceId, clinicId, isDark, onClick }) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;

  return (
    <button
      type="button"
      onClick={onClick}
      title="Ver criativo do anúncio"
      className={`mt-2 group relative block overflow-hidden rounded-lg border transition-all hover:shadow-md w-14 h-14 ${isDark ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-gray-100'}`}
    >
      <img
        src={`${API_URL}/metrics/ad_campaigns/media-by-source/${encodeURIComponent(sourceId)}?clinic_id=${clinicId}`}
        loading="lazy"
        alt="Criativo do anúncio"
        onError={() => setFailed(true)}
        className="h-full w-full object-cover transition-transform group-hover:scale-110"
      />
    </button>
  );
};

const ENTITY_LABEL: Record<string, string> = {
  lead: 'Lead',
  agendamento: 'Agendamento',
  comparecimento: 'Comparecimento',
  venda: 'Venda',
  noshow: 'Falta',
};

const deriveActorFromCrm = (e: CrmAuditEvent): { actorType: ActorType; actorLabel?: string } => {
  if (e.actor_type === 'ai_agent' || e.created_via === 'ai_agent' || e.created_via === 'agents_sdk') {
    return { actorType: 'ia', actorLabel: 'IA' };
  }
  if (e.actor_type === 'user' && (e.actor_user_name || e.actor_user_email)) {
    return { actorType: 'user', actorLabel: e.actor_user_name || e.actor_user_email || 'Atendente' };
  }
  if (e.actor_type === 'master' || e.actor_type === 'client') {
    return { actorType: 'master', actorLabel: e.actor_client_email || 'Gestor' };
  }
  if (e.actor_type === 'webhook' || e.created_via === 'webhook') {
    return { actorType: 'whatsapp', actorLabel: 'Webhook' };
  }
  return { actorType: 'system', actorLabel: 'Sistema' };
};

interface AliveSets {
  leadIds: Set<number>;
  agendamentoIds: Set<number>;
  comparecimentoIds: Set<number>;
  vendaIds: Set<number>;
  noshowIds: Set<number>;
}

const aliveSetFor = (entityType: string, alive: AliveSets): Set<number> | null => {
  switch (entityType) {
    case 'lead':
      return alive.leadIds;
    case 'agendamento':
      return alive.agendamentoIds;
    case 'comparecimento':
      return alive.comparecimentoIds;
    case 'venda':
      return alive.vendaIds;
    case 'noshow':
      return alive.noshowIds;
    default:
      return null;
  }
};

const mapCrmEvent = (e: CrmAuditEvent, alive: AliveSets): TimelineItem | null => {
  const entityLabel = ENTITY_LABEL[e.entity_type] || e.entity_type;
  const md = e.metadata || {};

  if (e.action === 'phone_corrected') {
    const actor = deriveActorFromCrm(e);
    const meta = [
      `Antes: ${typeof md.old_phone === 'string' ? md.old_phone : 'Não informado'}`,
      `Depois: ${typeof md.new_phone === 'string' ? md.new_phone : 'Não informado'}`,
    ];
    if (md.phone_verification?.status === 'verified') meta.push('Número verificado no WhatsApp da clínica');
    else if (md.phone_verification?.status === 'not_connected') meta.push('WhatsApp desconectado: número não verificado');
    return { id: `crm-${e.id}`, createdAt: e.created_at, icon: Phone, tone: 'brand',
      title: 'Telefone corrigido', metaLines: meta, actorType: actor.actorType, actorLabel: actor.actorLabel };
  }

  // CREATED: só mostra se a entidade NÃO está mais viva (foi excluída).
  // Se ainda está viva, evita duplicar com a fonte ativa (lead/agendamento/comp/venda/noshow).
  if (e.action === 'created') {
    const aliveSet = aliveSetFor(e.entity_type, alive);
    if (aliveSet && aliveSet.has(e.entity_id)) return null;

    let title: string;
    let icon: any;
    let tone: Tone;
    switch (e.entity_type) {
      case 'lead':
        title = 'Lead entrou no sistema';
        icon = DoorOpen;
        tone = 'brand';
        break;
      case 'agendamento':
        title = 'Consulta agendada';
        icon = Calendar;
        tone = 'brand';
        break;
      case 'comparecimento':
        title = 'Comparecimento registrado';
        icon = CheckCircle2;
        tone = 'success';
        break;
      case 'venda':
        title = 'Venda registrada';
        icon = DollarSign;
        tone = 'success';
        break;
      case 'noshow':
        title = 'Falta registrada';
        icon = XCircle;
        tone = 'danger';
        break;
      default:
        title = `${entityLabel} criado`;
        icon = Edit3;
        tone = 'neutral';
    }

    const meta: string[] = [];
    if (e.entity_type === 'agendamento' && md.consulta_data) {
      meta.push(`📅 Consulta: ${formatBR(String(md.consulta_data))}`);
    }
    if (e.entity_type === 'lead' && md.source_id) {
      meta.push(`Origem: ${md.source_id}`);
    }

    const actor = deriveActorFromCrm(e);
    return {
      id: `crm-${e.id}`,
      createdAt: e.created_at,
      icon,
      tone,
      title,
      metaLines: meta.length ? meta : undefined,
      actorType: actor.actorType,
      actorLabel: actor.actorLabel,
    };
  }

  // DELETED ou UPDATED
  const isDeletion = e.action === 'deleted';
  const actionLabel = isDeletion ? 'excluído' : (e.action === 'updated' ? 'atualizado' : e.action === 'marked_lost' ? 'marcado como perdido' : e.action);
  const title = `${entityLabel} ${actionLabel}`;
  const tone: Tone = isDeletion ? 'danger' : 'neutral';
  const icon: any = isDeletion ? Trash2 : Edit3;

  const meta: string[] = [];
  if (e.entity_type === 'agendamento' && md.consulta_data) {
    meta.push(`📅 Consulta: ${formatBR(String(md.consulta_data))}`);
  }
  if (e.entity_type === 'venda' && md.valor_faturado) {
    const v = typeof md.valor_faturado === 'number' ? formatBRL(md.valor_faturado) : String(md.valor_faturado);
    meta.push(`Faturado: ${v}`);
  }
  if (e.entity_type === 'comparecimento' && md.tratamento_orcado) {
    meta.push(`Tratamento: ${md.tratamento_orcado}`);
  }

  const actor = deriveActorFromCrm(e);
  return {
    id: `crm-${e.id}`,
    createdAt: e.created_at,
    icon,
    tone,
    title,
    metaLines: meta.length ? meta : undefined,
    actorType: actor.actorType,
    actorLabel: actor.actorLabel,
  };
};

const LeadTimeline: React.FC<LeadTimelineProps> = ({
  contactId,
  contactPhone,
  clinicId,
  leadId,
  refreshToken = 0,
}) => {
  const { isDark } = useTheme();
  const [items, setItems] = useState<TimelineItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adPreviewOpen, setAdPreviewOpen] = useState(false);
  const [adPreviewSourceId, setAdPreviewSourceId] = useState<string | null>(null);
  const loadGenerationRef = useRef(0);
  const auditPagesRef = useRef(1);
  const [hasMoreAudit, setHasMoreAudit] = useState(false);

  const load = useCallback(async () => {
    void refreshToken;
    const loadGeneration = loadGenerationRef.current + 1;
    loadGenerationRef.current = loadGeneration;
    setLoading(true);
    setError(null);
    const start = performance.now();
    console.log('[LeadTimeline] Carregando histórico', { contactId, contactPhone, clinicId, leadId });

    const userType = localStorage.getItem('user_type');
    const clientId =
      userType === 'user'
        ? parseInt(localStorage.getItem('master_client_id') || '0', 10)
        : parseInt(localStorage.getItem('client_id') || '0', 10);
    try {
      const [
        notesRes,
        auditRes,
        tasksRes,
        leadRes,
        agendaRes,
        compRes,
        vendasRes,
        nsRes,
        crmRes,
      ] = await Promise.allSettled([
        api.get(`/api/contacts/by-id/${contactId}/notes`),
        (async () => {
          const entries: AuditEntry[] = [];
          let hasMore = false;
          for (let page = 0; page < auditPagesRef.current; page += 1) {
            const response = await getContactAuditLog(clinicId, {
              contact_id: contactId, limit: 200, ...(page ? { offset: page * 200 } : {}),
            });
            const rows = auditRows(response);
            entries.push(...rows);
            hasMore = typeof response?.total === 'number'
              ? entries.length < response.total : rows.length === 200;
            if (!hasMore) break;
          }
          if (loadGenerationRef.current === loadGeneration) setHasMoreAudit(hasMore);
          return entries;
        })(),
        api.get(`/api/contacts/by-id/${contactId}/tasks`),
        leadId ? obterLead(clientId, clinicId, leadId) : Promise.resolve(null),
        leadId ? listarAgendamentos() : Promise.resolve([] as AgendamentoResponse[]),
        leadId ? listarComparecimentos(clientId, clinicId) : Promise.resolve([] as Comparecimento[]),
        leadId ? listarVendas(clientId, clinicId) : Promise.resolve([] as Venda[]),
        leadId ? listarNoShows(clientId, clinicId) : Promise.resolve([] as NoShowEvent[]),
        contactId ? getCrmAuditEvents(clinicId, contactPhone, 200, contactId) : Promise.resolve({ events: [] as CrmAuditEvent[], total: 0 }),
      ]);

      const all: TimelineItem[] = [];

      // 1) Lead criado
      if (leadRes.status === 'fulfilled' && leadRes.value) {
        const lead = leadRes.value as Lead;
        const at = lead.data_entrada || lead.created_at;
        if (at) {
          const actor = deriveActor(
            {},
            {
              created_by_type: lead.created_by_type,
              created_by_agent: lead.created_by_agent,
              created_via: lead.created_via,
              creator_name: lead.creator_name,
            },
            lead.source_id
          );
          all.push({
            id: `lead-${lead.id}`,
            createdAt: at,
            icon: DoorOpen,
            tone: 'brand',
            title: 'Lead entrou no sistema',
            metaLines: [lead.source_id ? `Origem: ${lead.source_id}` : ''].filter(Boolean),
            actorType: actor.actorType,
            actorLabel: actor.actorLabel,
            sourceId: lead.source_id || undefined,
          });
        }
      }

      // 2) Anotações
      const notesData: NoteResponse[] =
        notesRes.status === 'fulfilled' && Array.isArray(notesRes.value.data) ? notesRes.value.data : [];
      for (const n of notesData) {
        const actor = deriveActor(
          { user_name: n.created_by?.name },
          {
            created_by_type: n.created_by?.type,
            name: n.created_by?.name,
          }
        );
        all.push({
          id: `note-${n.id}`,
          createdAt: n.created_at,
          icon: StickyNote,
          tone: 'warning',
          title: 'Anotação criada',
          description: n.content,
          actorType: actor.actorType,
          actorLabel: actor.actorLabel,
        });
      }

      // 3 & 4) Tasks (regulares + scheduled_message)
      const tasksData: TaskItem[] =
        tasksRes.status === 'fulfilled' && Array.isArray(tasksRes.value.data) ? tasksRes.value.data : [];
      for (const t of tasksData) {
        const actor = deriveActor(
          { user_name: t.created_by?.name },
          {
            created_by_type: t.created_by?.type,
            name: t.created_by?.name,
            email: t.created_by?.email,
          }
        );
        if (t.task_type === 'scheduled_message') {
          all.push({
            id: `task-${t.id}`,
            createdAt: t.created_at,
            icon: Send,
            tone: 'brand',
            title: 'Mensagem agendada',
            description: t.message_content,
            metaLines: [
              `⏱ Disparo: ${formatBR(t.scheduled_for)}`,
              t.status === 'completed' ? '✓ Enviada' : t.status === 'canceled' ? '✗ Cancelada' : '⏳ Aguardando',
            ],
            actorType: actor.actorType,
            actorLabel: actor.actorLabel,
          });
        } else {
          all.push({
            id: `task-${t.id}`,
            createdAt: t.created_at,
            icon: Bell,
            tone: 'purple',
            title: `Tarefa criada: ${t.title}`,
            description: t.description,
            metaLines: [`⏱ Para: ${formatBR(t.scheduled_for)}`],
            actorType: actor.actorType,
            actorLabel: actor.actorLabel,
          });
        }
      }

      // 5) Audit log
      let auditEntries: AuditEntry[] = [];
      if (auditRes.status === 'fulfilled') {
        const data: any = auditRes.value;
        if (Array.isArray(data)) auditEntries = data;
        else if (Array.isArray(data?.audit_logs)) auditEntries = data.audit_logs;
        else if (Array.isArray(data?.entries)) auditEntries = data.entries;
        else if (Array.isArray(data?.logs)) auditEntries = data.logs;
      }
      for (let i = 0; i < auditEntries.length; i++) {
        const a = auditEntries[i];
        const details = parseDetails(a.action_details) || {};
        const nestedActor =
          details.actor && typeof details.actor === 'object'
            ? details.actor
            : {};
        const meta = formatActionType(a.action_type, details);
        const durableActorName =
          (typeof a.user_name === 'string' && a.user_name.trim()) ||
          (typeof nestedActor.name === 'string' && nestedActor.name.trim()) ||
          (typeof nestedActor.email === 'string' && nestedActor.email.trim()) ||
          (typeof details.actor_name === 'string' && details.actor_name.trim()) ||
          (typeof details.actor_display === 'string' && details.actor_display.trim()) ||
          (typeof details.actor_email === 'string' && details.actor_email.trim()) ||
          undefined;
        const actor = deriveActor(
          { user_name: a.user_name },
          {
            created_by_type:
              a.user_kind ||
              (typeof nestedActor.kind === 'string' ? nestedActor.kind : undefined),
            creator_name: durableActorName,
            email:
              (typeof nestedActor.email === 'string' && nestedActor.email) ||
              (typeof details.actor_email === 'string'
                ? details.actor_email
                : undefined),
          },
        );
        const isTagAction = a.action_type === 'tag_added' || a.action_type === 'tag_removed';
        const isFlowAction = a.action_type === 'flow_paused' || a.action_type === 'flow_resumed';
        const isModeAction =
          a.action_type === 'human_takeover' || a.action_type === 'release_to_bot';
        const isAttendanceAction = a.action_type.startsWith('attendance_');
        const isHumanOutbound = a.action_type === 'human_outbound';

        let description: string | undefined;
        if (isAttendanceAction) {
          const ownerName = typeof details.owner?.name === 'string'
            ? details.owner.name.trim() : '';
          const actorName = durableActorName || 'Um atendente';
          switch (a.action_type) {
            case 'attendance_claim':
              description = `${actorName} assumiu o atendimento.`;
              break;
            case 'attendance_release':
              description = `${actorName} liberou o atendimento para a fila.`;
              break;
            case 'attendance_transfer':
              description = ownerName
                ? `${actorName} transferiu o atendimento para ${ownerName}.`
                : `${actorName} transferiu o atendimento.`;
              break;
            case 'attendance_close':
              description = `${actorName} finalizou a conversa.`;
              break;
            case 'attendance_reopen':
              description = details.source === 'inbound'
                ? 'Uma nova mensagem do contato reabriu a conversa.'
                : `${actorName} reabriu a conversa.`;
              break;
            default:
              description = undefined;
          }
        } else if (isTagAction) {
          description = undefined;
        } else if (isFlowAction) {
          description = details.pause_reason || undefined;
        } else if (isModeAction) {
          const previousMode =
            details.previous_human_mode ?? details.previous_value ?? details.old_value;
          const nextMode =
            details.human_mode ?? details.new_human_mode ?? details.new_value;
          description =
            typeof previousMode === 'boolean' && typeof nextMode === 'boolean'
              ? `${previousMode ? 'Humano' : 'IA'} → ${nextMode ? 'Humano' : 'IA'}`
              : undefined;
        } else if (isHumanOutbound) {
          const source = typeof details.source === 'string' ? details.source : '';
          const messageType = typeof details.message_type === 'string'
            ? details.message_type
            : 'text';
          const sourceLabel = source === 'authenticated_chat'
            ? 'chat do CRM'
            : source === 'waha_app'
              ? 'WhatsApp conectado'
              : 'canal humano';
          const messageDescription = messageType === 'audio'
            ? 'Áudio enviado'
            : messageType === 'image'
              ? 'Imagem enviada'
              : messageType === 'video'
                ? 'Vídeo enviado'
                : messageType === 'document'
                  ? 'Documento enviado'
                  : 'Mensagem enviada';
          description = `${messageDescription} manualmente pelo ${sourceLabel}.`;
        } else {
          description = stringifyDetails(details);
        }

        all.push({
          id: `audit-${a.id ?? i}-${a.created_at}`,
          createdAt: a.created_at,
          icon: meta.icon,
          tone: meta.tone,
          title: meta.label,
          description,
          actorType: actor.actorType,
          actorLabel: actor.actorLabel,
        });
      }

      // 6) Agendamentos do lead
      if (leadId && agendaRes.status === 'fulfilled') {
        const agendamentos = (agendaRes.value as AgendamentoResponse[]).filter((a) => a.lead_id === leadId);
        for (const a of agendamentos) {
          const at = a.agendamento_realizado_em || a.consulta_data;
          if (!at) continue;
          const actor = deriveActor(
            {},
            {
              created_by_type: a.created_by_type,
              created_by_agent: a.created_by_agent,
              created_via: a.created_via,
              creator_name: a.creator_name,
            }
          );
          all.push({
            id: `appt-${a.id}`,
            createdAt: at,
            icon: Calendar,
            tone: 'brand',
            title: 'Consulta agendada',
            metaLines: [
              a.consulta_data ? `📅 Para: ${formatBR(a.consulta_data)}` : '',
              a.midia ? `Origem: ${a.midia}` : '',
              a.interesse ? `Interesse: ${a.interesse}` : '',
            ].filter(Boolean),
            actorType: actor.actorType,
            actorLabel: actor.actorLabel,
          });
        }
      }

      // 7) Comparecimentos do lead
      if (leadId && compRes.status === 'fulfilled') {
        const comps = (compRes.value as Comparecimento[]).filter((c) => c.lead_id === leadId);
        for (const c of comps) {
          const actor = deriveActor(
            {},
            {
              created_by_type: c.created_by_type,
              created_by_agent: c.created_by_agent,
              created_via: c.created_via,
              creator_name: c.creator_name,
            }
          );
          all.push({
            id: `comp-${c.id}`,
            createdAt: c.compareceu_em,
            icon: CheckCircle2,
            tone: 'success',
            title: 'Comparecimento registrado',
            metaLines: [
              c.tratamento_orcado ? `Tratamento: ${c.tratamento_orcado}` : '',
              c.valor_orcamento ? `Orçamento: ${formatBRL(c.valor_orcamento)}` : '',
            ].filter(Boolean),
            actorType: actor.actorType,
            actorLabel: actor.actorLabel,
          });
        }
      }

      // 8) No-shows do lead
      if (leadId && nsRes.status === 'fulfilled') {
        const noshows = (nsRes.value as NoShowEvent[]).filter((n) => n.lead_id === leadId);
        for (const n of noshows) {
          const actor = deriveActor(
            {},
            {
              created_by_type: n.created_by_type,
              created_by_agent: n.created_by_agent,
              created_via: n.created_via,
              creator_name: n.creator_name,
            }
          );
          all.push({
            id: `ns-${n.id}`,
            createdAt: n.marcado_em,
            icon: XCircle,
            tone: 'danger',
            title: 'Falta registrada',
            description: n.observacao,
            actorType: actor.actorType,
            actorLabel: actor.actorLabel,
          });
        }
      }

      // 9) Vendas do lead
      if (leadId && vendasRes.status === 'fulfilled') {
        const vendas = (vendasRes.value as Venda[]).filter((v) => v.lead_id === leadId);
        for (const v of vendas) {
          const actor = deriveActor(
            {},
            {
              created_by_type: v.created_by_type,
              created_by_agent: v.created_by_agent,
              created_via: v.created_via,
              creator_name: v.creator_name,
            }
          );
          all.push({
            id: `venda-${v.id}`,
            createdAt: v.venda_data || v.updated_at || '',
            icon: DollarSign,
            tone: 'success',
            title: 'Venda registrada',
            metaLines: [
              v.tratamento_fechado ? `Tratamento: ${v.tratamento_fechado}` : '',
              v.valor_faturado ? `Faturado: ${formatBRL(v.valor_faturado)}` : '',
              v.valor_pago ? `Pago: ${formatBRL(v.valor_pago)}` : '',
            ].filter(Boolean),
            actorType: actor.actorType,
            actorLabel: actor.actorLabel,
          });
        }
      }

      // 10) Eventos do CRM (criações de entidades excluídas + deleções + updates)
      // Funciona mesmo após o lead ter sido excluído — busca por phone (durável).
      if (crmRes.status === 'fulfilled') {
        // Construir sets de entidades vivas para deduplicar com fontes ativas.
        const aliveLeadIds = new Set<number>();
        if (leadRes.status === 'fulfilled' && leadRes.value) {
          aliveLeadIds.add((leadRes.value as Lead).id);
        }
        const aliveAgendamentoIds = new Set<number>();
        if (leadId && agendaRes.status === 'fulfilled') {
          for (const a of (agendaRes.value as AgendamentoResponse[]).filter((x) => x.lead_id === leadId)) {
            aliveAgendamentoIds.add(a.id);
          }
        }
        const aliveComparecimentoIds = new Set<number>();
        if (leadId && compRes.status === 'fulfilled') {
          for (const c of (compRes.value as Comparecimento[]).filter((x) => x.lead_id === leadId)) {
            aliveComparecimentoIds.add(c.id);
          }
        }
        const aliveVendaIds = new Set<number>();
        if (leadId && vendasRes.status === 'fulfilled') {
          for (const v of (vendasRes.value as Venda[]).filter((x) => x.lead_id === leadId)) {
            aliveVendaIds.add(v.id);
          }
        }
        const aliveNoshowIds = new Set<number>();
        if (leadId && nsRes.status === 'fulfilled') {
          for (const n of (nsRes.value as NoShowEvent[]).filter((x) => x.lead_id === leadId)) {
            aliveNoshowIds.add(n.id);
          }
        }
        const alive = {
          leadIds: aliveLeadIds,
          agendamentoIds: aliveAgendamentoIds,
          comparecimentoIds: aliveComparecimentoIds,
          vendaIds: aliveVendaIds,
          noshowIds: aliveNoshowIds,
        };

        const events = (crmRes.value as { events: CrmAuditEvent[] }).events || [];
        for (const ev of events) {
          const item = mapCrmEvent(ev, alive);
          if (item) all.push(item);
        }
      }

      // Ordenar desc
      const sorted = all
        .filter((x) => x.createdAt)
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

      console.log('[LeadTimeline] Itens carregados:', {
        total: sorted.length,
        ms: Math.round(performance.now() - start),
      });
      if (loadGenerationRef.current !== loadGeneration) return;
      setItems(sorted);
    } catch (err: any) {
      console.error('[LeadTimeline] Erro ao carregar histórico:', err);
      if (loadGenerationRef.current !== loadGeneration) return;
      setError('Erro ao carregar histórico');
    } finally {
      if (loadGenerationRef.current === loadGeneration) {
        setLoading(false);
      }
    }
  }, [contactId, contactPhone, clinicId, leadId, refreshToken]);

  useEffect(() => {
    auditPagesRef.current = 1;
  }, [contactId, clinicId]);

  useEffect(() => {
    const refresh = (event: Event) => {
      const changedContactId = (event as CustomEvent).detail?.contactId;
      if (!changedContactId || Number(changedContactId) === contactId) void load();
    };
    window.addEventListener('contact-attendance-changed', refresh);
    return () => window.removeEventListener('contact-attendance-changed', refresh);
  }, [contactId, load]);

  useEffect(() => {
    load();
    return () => {
      loadGenerationRef.current += 1;
    };
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-sminy-primary/30 border-t-sminy-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <div
          className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3 ${
            isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-500'
          }`}
        >
          <AlertCircle className="w-6 h-6" />
        </div>
        <p className={`text-sm mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{error}</p>
        <button
          onClick={load}
          className="rounded-xl bg-sminy-primary px-4 py-2 text-sm font-medium text-white hover:bg-sminy-primary/90 transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <p className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          {items.length} {items.length === 1 ? 'evento' : 'eventos'}
        </p>
        <button
          onClick={load}
          title="Recarregar"
          className={`p-1.5 rounded-xl transition-colors ${
            isDark
              ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/40'
              : 'text-gray-500 hover:text-gray-800 hover:bg-gray-100'
          }`}
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-4">
          <div
            className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 ${
              isDark ? 'bg-gray-700' : 'bg-gray-100'
            }`}
          >
            <Clock className={`w-8 h-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          </div>
          <h3 className={`text-sm font-semibold mb-1 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            Nenhum evento registrado
          </h3>
          <p className={`text-xs text-center max-w-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Anotações, tarefas, agendamentos e ações sobre este contato aparecerão aqui em ordem cronológica.
          </p>
        </div>
      ) : (
        <div className="px-4 pb-4">
          <ol className="relative">
            {/* Linha vertical contínua */}
            <span
              className={`absolute left-[15px] top-2 bottom-2 w-px ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}
              aria-hidden="true"
            />

            {items.map((item, idx) => {
              const Icon = item.icon || ShieldAlert;
              if (!item.icon) {
                console.warn('[LeadTimeline] Item sem ícone:', item);
              }
              const date = parseISO(item.createdAt);
              const isLast = idx === items.length - 1;

              return (
                <li key={item.id} className={`relative pl-10 ${isLast ? '' : 'pb-4'}`}>
                  {/* Dot na linha */}
                  <span
                    className={`absolute left-[10px] top-2 w-3 h-3 rounded-full ring-4 ${toneRingClasses(
                      item.tone,
                      isDark
                    )}`}
                    aria-hidden="true"
                  />

                  {/* Card do evento */}
                  <div
                    className={`rounded-2xl border p-3 transition-colors ${
                      isDark
                        ? 'bg-gray-800 border-gray-700 hover:border-gray-600'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-start gap-2 mb-1.5">
                      <div
                        className={`flex-shrink-0 w-7 h-7 rounded-xl flex items-center justify-center ${toneIconBg(
                          item.tone,
                          isDark
                        )}`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4
                          className={`text-sm font-semibold leading-tight break-words [overflow-wrap:anywhere] ${
                            isDark ? 'text-gray-100' : 'text-gray-900'
                          }`}
                        >
                          {item.title}
                        </h4>
                        <p
                          className={`text-[11px] mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                          title={format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                        >
                          {format(date, "dd/MM/yyyy · HH:mm", { locale: ptBR })}
                          <span className="mx-1">·</span>
                          {formatDistanceToNow(date, { locale: ptBR, addSuffix: true })}
                        </p>
                      </div>
                    </div>

                    {item.description && (
                      <p
                        className={`text-sm whitespace-pre-wrap break-words mb-2 px-2 py-1.5 rounded-lg border-l-2 border-sminy-primary ${
                          isDark ? 'bg-gray-900/40 text-gray-300' : 'bg-gray-50 text-gray-700'
                        }`}
                      >
                        {item.description}
                      </p>
                    )}

                    {item.metaLines && item.metaLines.length > 0 && (
                      <div className={`text-xs space-y-0.5 mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {item.metaLines.map((line, i) => (
                          <p key={i}>{line}</p>
                        ))}
                      </div>
                    )}

                    {item.actorType === 'meta_ads' && item.sourceId && clinicId > 0 && (
                      <EventAdThumbnail
                        sourceId={item.sourceId}
                        clinicId={clinicId}
                        isDark={isDark}
                        onClick={() => {
                          setAdPreviewSourceId(item.sourceId!);
                          setAdPreviewOpen(true);
                        }}
                      />
                    )}

                    <div className="flex items-center justify-end">
                      <ActorBadge actorType={item.actorType} label={item.actorLabel} isDark={isDark} />
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {hasMoreAudit && (
        <button
          type="button"
          onClick={() => { auditPagesRef.current += 1; void load(); }}
          className="mx-4 mb-4 rounded-xl border border-sminy-primary/30 px-4 py-2 text-sm font-medium text-sminy-primary hover:bg-sminy-primary/10"
        >
          Carregar eventos anteriores de atendimento e contato
        </button>
      )}

      <ImagePreviewModal
        isOpen={adPreviewOpen}
        imageUrl={
          adPreviewSourceId && clinicId > 0
            ? `${API_URL}/metrics/ad_campaigns/media-by-source/${encodeURIComponent(adPreviewSourceId)}?clinic_id=${clinicId}`
            : null
        }
        onClose={() => {
          setAdPreviewOpen(false);
          setAdPreviewSourceId(null);
        }}
        title={adPreviewSourceId ? `Anúncio · ${adPreviewSourceId}` : 'Visualização do Anúncio'}
      />
    </div>
  );
};

export default LeadTimeline;
